<template>
  <div class="archive-reports-list">
    <cl-data-table
      :data-test-id="'archive-reports-data-table'"
      :fetching="fetching"
      :headers="tableHeaders"
      :items="items"
      :total="serverTotal"
      :default-sort-by="sortByCookie"
      :default-sort-desc="sortDirCookie"
      :default-items-on-page="itemsPerPageCookie"
      with-delete
      with-select
      with-set-columns
      :searchPlaceholder="$t('Search')"
      @change-page="changePage"
      @search="debouncedSearch"
      @on-delete-items="onDelete"
      @change-items-on-page="onChangeItemsOnPageClick"
      @sort-items="onSortItemsClick"
      @change-selected-columns="setSelectedColumnsCookie"
    >
      <template v-slot:[`body.cell.actions`]="props">
        <cl-data-table-more-action>
          <cl-data-table-more-action-item
            v-if="props.trItem.format.includes('html')"
            @on-click="onView(props.trItem)"
            class="tw-cursor-pointer hover:tw-text-secondary-500"
            >{{ $t("View") }} HTML</cl-data-table-more-action-item
          >
          <cl-data-table-more-action-item
            v-if="props.trItem.format.includes('pdf')"
            @on-click="onReportAction(props.trItem, 'pdf')"
            class="tw-cursor-pointer hover:tw-text-secondary-500"
            >{{ $t("Download") }} PDF</cl-data-table-more-action-item
          >
          <cl-data-table-more-action-item
            v-if="props.trItem.format.includes('csv')"
            @on-click="onReportAction(props.trItem, 'csv')"
            class="tw-cursor-pointer hover:tw-text-secondary-500"
            >{{ $t("Download") }} CSV</cl-data-table-more-action-item
          >
          <cl-data-table-more-action-item
            v-if="props.trItem.format.includes('txt')"
            @on-click="onReportAction(props.trItem, 'txt')"
            class="tw-cursor-pointer hover:tw-text-secondary-500"
            >{{ $t("Download") }} TXT</cl-data-table-more-action-item
          >
        </cl-data-table-more-action>
      </template>
    </cl-data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";

import {
  REPORT_DOMAIN_TYPE,
  REPORT_CUSTOMER_TYPE,
  TIER,
  DATE_FORMAT,
  DEFAULT_OPTIONS,
} from "@/constants";
import { useDataTable } from "@/composables/useDataTable";
import { useDataTableCookies } from "@/composables/useDataTableCookies";

export default {
  name: "ArchiveReportsList",
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    tier: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const cookieKey = `reporting-archive-${props.tier || "customer"}`;
    const {
      sortByCookie,
      sortDirCookie,
      itemsPerPageCookie,
      selectedColumnsCookie,
      setSortItemsCookie,
      setItemsPerPageCookie,
      setSelectedColumnsCookie,
    } = useDataTableCookies(
      {
        sortByKey: `${cookieKey}-sort-by`,
        sortDirKey: `${cookieKey}-sort-dir`,
        itemsPerPageKey: `${cookieKey}-items-per-page`,
        selectedColumnsKey: `${cookieKey}-selected-columns`,
      },
      {
        ...DEFAULT_OPTIONS,
        sortBy: "id",
      }
    );

    const optionsBasedOnCookies = {
      sortBy: sortByCookie,
      sortDir: sortDirCookie,
      results: itemsPerPageCookie,
      page: 1,
    };

    const {
      dataOptions,
      sortItems,
      changePage,
      changeItemsPerPage,
      debouncedSearch,
    } = useDataTable(optionsBasedOnCookies);

    return {
      dataOptions,
      debouncedSearch,
      sortItems,
      changePage,
      changeItemsPerPage,
      selectedColumnsCookie,
      setSortItemsCookie,
      setItemsPerPageCookie,
      setSelectedColumnsCookie,
      sortByCookie,
      sortDirCookie,
      itemsPerPageCookie,
    };
  },
  data() {
    return {
      fetching: false,
      items: [],
    };
  },
  computed: {
    ...mapGetters("archiveReports", ["reports", "serverTotal"]),
    tableHeaders() {
      const headers = [
        { key: "id", label: this.$t("ID"), sortable: true },
        { key: "period", label: this.$t("Period"), sortable: false },
        { key: "type", label: this.$t("Type"), sortable: true },
        { key: "domain", label: this.$t("Domain"), sortable: false },
        { key: "created_at", label: this.$t("Created"), sortable: true },
        { key: "actions", label: this.$t("Actions"), sortable: false },
      ];

      if (!this.selectedColumnsCookie?.length) {
        return headers;
      }

      return headers.map((header) => ({
        ...header,
        enabled: this.selectedColumnsCookie.includes(header.key),
      }));
    },
  },
  watch: {
    dataOptions: {
      handler() {
        this.fetchNewReports();
      },
      deep: true,
      immediate: true,
    },
    reports: {
      immediate: true,
      handler() {
        this.items = this.reports.map(this.mapReports);
      },
    },
  },
  methods: {
    ...mapActions("archiveReports", [
      "fetchReports",
      "batchDeleteReports",
      "clearCache",
      "fetchReportPDF",
      "fetchReportCSV",
      "fetchReportTXT",
    ]),
    onSortItemsClick(by, dir) {
      this.setSortItemsCookie(by, dir);
      this.sortItems(by, dir);
    },

    onChangeItemsOnPageClick(number) {
      this.setItemsPerPageCookie(number);
      this.changeItemsPerPage(number);
    },
    mapReports(report) {
      const periods = {
        all: this.$t("All"),
        today: this.$t("Today"),
        yesterday: this.$t("Yesterday"),
        last_7_days: this.$t("Last 7 days"),
        this_month: this.$t("This Month"),
        last_month: this.$t("Last Month"),
      };

      return {
        ...report,
        period: periods[report.period],
        domain: report.domain || "---",
        type:
          this.tier === TIER.domains
            ? REPORT_DOMAIN_TYPE[report.type]
            : REPORT_CUSTOMER_TYPE[report.type],
        created_at: `${dayjs(report.created_at).format(DATE_FORMAT.SHORT)}`,
      };
    },
    async onReportAction(action, download_type) {
      const title = `${action.type}_${dayjs(action.created_at).format(
        "YYYY-MM-DD"
      )}`;

      if (download_type === "txt") {
        await this.fetchReportTXT({
          tier: this.tier,
          id: this.id,
          reportId: action.id,
          title: `${title}`,
        });
      }
      if (download_type === "csv") {
        await this.fetchReportCSV({
          tier: this.tier,
          id: this.id,
          reportId: action.id,
          title: `${title}`,
        });
      }
      if (download_type === "pdf") {
        await this.fetchReportPDF({
          tier: this.tier,
          id: this.id,
          reportId: action.id,
          title: `${title}`,
        });
      }
    },
    onView(report) {
      if (this.tier === TIER.domainGroups) {
        this.$router.push({
          name: "customer-reports-archive-preview",
          params: {
            id: report.id,
          },
        });
      }

      if (this.tier === TIER.domains) {
        this.$router.push({
          name: "domain-reports-archive-preview",
          params: {
            id: report.id,
          },
        });
      }
    },
    async onDelete(reports) {
      try {
        this.fetching = true;

        await this.batchDeleteReports({
          tier: this.tier,
          id: this.id,
          reports,
        });
        if (this.reports.length === 0 && this.dataOptions.page > 0) {
          this.dataOptions.page = this.dataOptions.page - 1;
        }
      } catch (_err) {
        // stub
      } finally {
        this.fetching = false;
      }
    },
    async fetchNewReports() {
      try {
        this.fetching = true;

        await this.fetchReports({
          tier: this.tier,
          id: this.id,
          ...this.dataOptions,
        });
      } catch (_err) {
        // stub
      } finally {
        this.fetching = false;
      }
    },
  },
  created() {
    this.clearCache();
  },
};
</script>
